import React from 'react';

import { isNil } from 'es-toolkit';
import { toInteger } from 'es-toolkit/compat';
import { useLocation } from 'react-router';

import { Nullable } from '@clearverify/types';

import { SortOption } from 'src/types/sort';

import { DEFAULT_SORT_OPTION_VALUE } from '../FilterProvider';
import {
  DEFAULT_PAGE,
  DEFAULT_ROWS_PER_PAGE,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from '../PaginationProvider';

import {
  FilterItem,
  FilterStorage,
  PaginationItem,
  PaginationStorage,
  SortOptionStorage,
} from './types';

export const useFilterStateStorage = () => {
  const [filterStorage, setFilterStorage] = React.useState<FilterStorage>({});

  const getFilterByKey = React.useCallback(
    (key: string) => {
      return filterStorage[key] || {};
    },
    [filterStorage],
  );

  const setFilterByKey = React.useCallback(
    (key: string, filter: FilterItem) => {
      setFilterStorage({
        ...filterStorage,
        [key]: filter,
      });
    },
    [filterStorage],
  );

  const resetFilterStorage = React.useCallback(() => {
    setFilterStorage({});
  }, []);

  return {
    filterStorage,
    getFilterByKey,
    setFilterByKey,
    resetFilterStorage,
  };
};

export const useSortStateStorage = () => {
  const [sortOptionStorage, setSortOptionStorage] = React.useState<SortOptionStorage>({});

  const getSortOptionByKey = React.useCallback(
    (key: string) => {
      return sortOptionStorage[key] || DEFAULT_SORT_OPTION_VALUE;
    },
    [sortOptionStorage],
  );

  const setSortOptionByKey = React.useCallback(
    (key: Nullable<string>, sortOption: Nullable<SortOption>) => {
      if (!key) return;
      if (isNil(sortOption)) {
        const sortOptionStorageCopy = structuredClone(sortOptionStorage);
        if (sortOptionStorageCopy[key]) {
          delete sortOptionStorageCopy[key];
        }
        setSortOptionStorage(sortOptionStorageCopy);

        return;
      }

      setSortOptionStorage({
        ...sortOptionStorage,
        [key]: sortOption,
      });
    },
    [sortOptionStorage],
  );

  const resetSortOptionStorage = React.useCallback(() => {
    setSortOptionStorage({});
  }, []);

  return {
    sortOptionStorage,
    getSortOptionByKey,
    setSortOptionByKey,
    resetSortOptionStorage,
  };
};

export const usePaginationStateStorage = () => {
  const { pathname } = useLocation();
  const [paginationStorage, setPaginationStorage] = React.useState<PaginationStorage>({});

  const getPaginationByKey = React.useCallback(
    (key: string) => {
      return (
        paginationStorage[key] || {
          page: DEFAULT_PAGE,
          rowsPerPage: DEFAULT_ROWS_PER_PAGE,
          rowsPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS,
        }
      );
    },
    [paginationStorage],
  );

  const setPaginationByKey = React.useCallback(
    (key: string, pagination: PaginationItem) => {
      setPaginationStorage({
        ...paginationStorage,
        [key]: pagination,
      });
    },
    [paginationStorage],
  );

  const resetPageByKey = React.useCallback(
    (key: string) => {
      const pagination = paginationStorage[key] || {
        page: DEFAULT_PAGE,
        rowsPerPage: DEFAULT_ROWS_PER_PAGE,
        rowsPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS,
      };
      setPaginationStorage({
        ...paginationStorage,
        [key]: {
          ...pagination,
          page: DEFAULT_PAGE,
        },
      });
    },
    [paginationStorage],
  );

  const setPageByKey = React.useCallback(
    (key: string, page: number) => {
      const pagination = paginationStorage[key] || {
        page: DEFAULT_PAGE,
        rowsPerPage: DEFAULT_ROWS_PER_PAGE,
        rowsPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS,
      };
      setPaginationStorage({
        ...paginationStorage,
        [key]: {
          ...pagination,
          page,
        },
      });
    },
    [paginationStorage],
  );

  const setRowsPerPageByKey = React.useCallback(
    (key: string, rowsPerPage: number) => {
      const pagination = paginationStorage[key] || {
        page: DEFAULT_PAGE,
        rowsPerPage: DEFAULT_ROWS_PER_PAGE,
        rowsPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS,
      };
      setPaginationStorage({
        ...paginationStorage,
        [key]: {
          ...pagination,
          page: DEFAULT_PAGE,
          rowsPerPage,
        },
      });
    },
    [paginationStorage],
  );

  const resetPaginationStorage = React.useCallback(() => {
    setPaginationStorage({});
  }, []);

  const getPaginationObjectByKey = React.useCallback(
    (key: string) => {
      const pagination = paginationStorage[key] || {
        page: DEFAULT_PAGE,
        rowsPerPage: DEFAULT_ROWS_PER_PAGE,
        rowsPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS,
      };
      return {
        ...pagination,
        onModelChange(page: number, rowsPerPage: number) {
          setPaginationStorage({
            ...paginationStorage,
            [key]: {
              ...pagination,
              page: rowsPerPage !== pagination.rowsPerPage ? DEFAULT_PAGE : page,
              rowsPerPage,
            },
          });
        },
        resetPage() {
          setPaginationStorage({
            ...paginationStorage,
            [key]: {
              ...pagination,
              page: DEFAULT_PAGE,
            },
          });
        },
      };
    },
    [paginationStorage],
  );

  const getPaginationQueryVariablesByKey = React.useCallback(
    (key: string) => {
      const pagination = getPaginationByKey(key);
      return {
        skip: toInteger(pagination.page) * toInteger(pagination.rowsPerPage),
        first: toInteger(pagination.rowsPerPage),
      };
    },
    [getPaginationByKey],
  );

  const currentPagination = React.useMemo(() => {
    const pagination = paginationStorage[pathname] || {
      page: DEFAULT_PAGE,
      rowsPerPage: DEFAULT_ROWS_PER_PAGE,
      rowsPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS,
    };
    return {
      ...pagination,
      onModelChange: (page: number, rowsPerPage: number) => {
        setPaginationStorage({
          ...paginationStorage,
          [pathname]: {
            ...pagination,
            page: rowsPerPage !== pagination.rowsPerPage ? DEFAULT_PAGE : page,
            rowsPerPage,
          },
        });
      },
      resetPage: () => {
        setPaginationStorage({
          ...paginationStorage,
          [pathname]: {
            ...pagination,
            page: DEFAULT_PAGE,
          },
        });
      },
    };
  }, [paginationStorage, pathname]);

  const currentPaginationQueryVariables = React.useMemo(() => {
    return {
      skip: toInteger(currentPagination.page) * toInteger(currentPagination.rowsPerPage),
      first: toInteger(currentPagination.rowsPerPage),
    };
  }, [currentPagination.page, currentPagination.rowsPerPage]);

  return {
    paginationStorage,
    currentPagination,
    currentPaginationQueryVariables,
    getPaginationQueryVariablesByKey,
    getPaginationObjectByKey,
    getPaginationByKey,
    setPaginationByKey,
    resetPageByKey,
    setPageByKey,
    setRowsPerPageByKey,
    resetPaginationStorage,
  };
};
